import './account-create.component.scss';

import { h } from 'preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';

import { useTranslation } from '../../../i18n';
import type { AccountSettingsResponse } from '../../../models/common.model';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import type { AuthenticationResult } from '../../models/auth.model';
import { AuthenticationFormErrorCode } from '../../models/auth.model';
import { authenticationFormService } from '../../services/authentication-form.service';
import { selectAccountSettings, selectIsLegacyAccount } from '../../store/auth.selectors';
import { loadAccountSettings } from '../../store/auth.thunk';

type Props = {
  returnToUrl: string;
  terms?: string;
  onClose?: (result: AuthenticationResult) => void;
  onShowLogin?: () => void;
  onShowVerifyEmail?: (email: string) => void;
};

export function AccountCreateForm({
  returnToUrl,
  terms,
  onShowLogin,
  onClose,
  onShowVerifyEmail,
}: Props) {
  const actionUrl = '/account';
  const dispatch = useAppDispatch();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [acceptsTerms, setAcceptsTerms] = useState(false);
  const accountSettings: AccountSettingsResponse = useAppSelector(selectAccountSettings);
  const isLegacyAccount = useAppSelector(selectIsLegacyAccount);

  const formRef = useRef<HTMLFormElement>(null);
  const { t } = useTranslation();

  // todayMinus18 in yyyy-mm-dd
  const todayMinus18 = useMemo(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 18);
    return d.toISOString().split('T')[0];
  }, []);

  useEffect(() => {
    dispatch(loadAccountSettings());
  }, [dispatch]);

  const displayTerms = (e: Event) => {
    e.preventDefault();
    e.stopPropagation();
    setShowTerms(true);
  };

  const handleCreateAcct = async (event: Event) => {
    event.preventDefault();
    event.stopPropagation();

    const form = formRef.current!;
    const getVal = (sel: string) =>
      (form.querySelector(sel) as HTMLInputElement | null)?.value?.trim();

    const firstName = getVal('#first-name') || '';
    const lastName = getVal('#last-name') || '';
    const email = getVal('#email') || '';
    const password = getVal('#password') || '';
    const countryCode = (getVal('#countryCode') || '+1').replace(/\s+/g, '');
    const phoneRaw = getVal('#phone') || '';
    const birthday = getVal('#birthday') || '';
    const zipcode = getVal('#zipcode') || '';
    const acceptsMarketing = (form.querySelector('#accepts-marketing') as HTMLInputElement | null)
      ?.checked
      ? 'true'
      : 'false';

    if (!email || !password) {
      setErrorMessage(t('auth.errorRequiredFields'));
      console.error('Validation failed: Missing fields');
      return false;
    }

    const body = new URLSearchParams();
    body.set('form_type', 'create_customer');
    body.set('utf8', '✓');
    body.set('return_to', returnToUrl);
    body.set('customer[email]', email);
    body.set('customer[password]', password);

    if (firstName) body.set('customer[first_name]', firstName);
    if (lastName) body.set('customer[last_name]', lastName);

    if (phoneRaw) {
      const phoneDigits = phoneRaw.replace(/\D/g, '');
      const cc = countryCode.startsWith('+') ? countryCode : `+${countryCode}`;
      body.set('customer[note][phone]', `${cc}${phoneDigits}`);
    }
    if (birthday) body.set('customer[note][birthday]', birthday);
    if (zipcode) body.set('customer[note][zipcode]', zipcode);

    setLoading(true);
    setErrorMessage(null);

    await authenticationFormService.submitForm(actionUrl, returnToUrl, form, body, (response) => {
      if (response.type === 'ERROR_CODE') {
        if (response.errorCode === AuthenticationFormErrorCode.EMAIL_VERIFY) {
          // TODO
          onShowVerifyEmail?.(email);
        } else {
          setErrorMessage(response.errorMessage || t('auth.errorCreateGeneric'));
        }
        if (
          response.errorCode === AuthenticationFormErrorCode.UNKNOWN_ERROR &&
          (response as any).error
        ) {
          setErrorMessage(t('auth.errorCreateAccount'));
          console.error('Login error:', (response as any).error ?? (response as any).response);
        }
      } else if (response.type === 'SUCCESS') {
        onClose?.({
          success: true,
          termsAccepted: acceptsTerms,
          optInToMarketingEmails: acceptsMarketing === 'true',
        });
      }
      setLoading(false);
    });

    return false;
  };

  const acctSettings = accountSettings?.accountSettings;

  return (
    <form
      id="create_customer"
      accept-charset="UTF-8"
      className={`single-account-create-form ${errorMessage ? 'single-has-error' : ''}`}
      onSubmit={handleCreateAcct}
      ref={formRef}
      noValidate
    >
      {errorMessage && (
        <div className="single-error-container">
          <div dangerouslySetInnerHTML={{ __html: errorMessage }} />
        </div>
      )}

      {!showTerms && acctSettings && (
        <section className={`${showTerms ? 'single-d-none' : ''}`}>
          <div className="single-input-group-two-col">
            {acctSettings.showFirstName && (
              <div className="single-input-group">
                <label htmlFor="first-name">{t('auth.firstName')}</label>
                <input
                  id="first-name"
                  type="text"
                  name="customer[first_name]"
                  autoComplete="given-name"
                  placeholder={t('auth.firstName')}
                  required
                />
              </div>
            )}

            {acctSettings.showLastName && (
              <div className="single-input-group">
                <label htmlFor="last-name">{t('auth.lastName')}</label>
                <input
                  id="last-name"
                  type="text"
                  name="customer[last_name]"
                  autoComplete="family-name"
                  placeholder={t('auth.lastName')}
                  required
                />
              </div>
            )}
          </div>

          <div className="single-input-group">
            <label htmlFor="email">{t('auth.email')}</label>
            <input
              id="email"
              type="email"
              name="customer[email]"
              autoComplete="email"
              placeholder={t('auth.email')}
              required
            />
          </div>

          <div className="single-input-group">
            <label htmlFor="password">{t('auth.password')}</label>
            <input
              id="password"
              type="password"
              name="customer[password]"
              placeholder={t('auth.password')}
              required
            />
          </div>

          {acctSettings.showZipCodeField && (
            <div className="single-input-group">
              <label htmlFor="zipcode">{t('auth.zipcodeOptional')}</label>
              <input
                id="zipcode"
                type="text"
                name="customer[note][zipcode]"
                placeholder={t('auth.zipcodePlaceholder')}
              />
            </div>
          )}

          <div className="single-input-group-two-col">
            {acctSettings.showPhoneField && (
              <div className="single-input-group">
                <label htmlFor="phone">{t('auth.phoneNumberOptional')}</label>
                <div className="phone-with-country-code">
                  <input
                    type="text"
                    id="countryCode"
                    name="countryCode"
                    value="+1"
                    placeholder="+1"
                    maxLength={4}
                  />
                  <input
                    id="phone"
                    type="text"
                    name="customer[note][phone]"
                    autoComplete="tel"
                    placeholder={t('auth.phoneNumber')}
                  />
                </div>
              </div>
            )}

            {acctSettings.showBirthdayField && (
              <div className="single-input-group">
                <label htmlFor="birthday">{t('auth.birthdayOptional')}</label>
                <input
                  id="birthday"
                  type="date"
                  name="customer[note][birthday]"
                  autoComplete="birthday"
                  placeholder={t('auth.birthday')}
                  max={todayMinus18}
                />
              </div>
            )}
          </div>

          {isLegacyAccount && acctSettings.showMarketingOptIn && (
            <div className="single-input-group single-checkbox">
              <input id="accepts-marketing" type="checkbox" name="customer[accepts_marketing]" />
              <label htmlFor="accepts-marketing">{t('auth.subscribeToMarketing')}</label>
            </div>
          )}

          {terms && (
            <div className="single-input-group single-checkbox">
              <input
                id="accepts-terms"
                type="checkbox"
                name="customer[accepts_terms]"
                onChange={(e) => setAcceptsTerms((e.target as HTMLInputElement).checked)}
              />
              <label htmlFor="accepts-terms">
                {t('auth.acceptTerms')}{' '}
                <span className="terms-link cursor-pointer" onClick={displayTerms}>
                  {t('auth.termsAndConditions')}
                </span>
              </label>
            </div>
          )}
        </section>
      )}

      {showTerms && (
        <div>
          <span className="go-back cursor-pointer" onClick={() => setShowTerms(false)}>
            {t('auth.back')}
          </span>
          <div className="legal-terms" dangerouslySetInnerHTML={{ __html: terms || '' }} />
        </div>
      )}

      {!showTerms && (
        <>
          <button
            className="btn button single-submit-btn"
            disabled={loading || (!!terms && !acceptsTerms)}
            type="submit"
          >
            {loading ? t('auth.creating') : t('auth.create')}
          </button>
          <p className="login-text">
            {t('auth.alreadyHaveAccount')}{' '}
            <span class="cursor-pointer" onClick={() => onShowLogin?.()}>
              {t('auth.logIn')}
            </span>
          </p>
        </>
      )}
    </form>
  );
}
